import moment from 'moment';
import { STATUSES } from 'constants/AppConstants';
import { SITEMAP_SUBMITTED_ROUTE } from 'constants/RouteConstants';
import { SUB_STATUSES } from 'features/orderHistory/OrderHistory.constants';
import { formatAddress } from 'features/order/Order.utils';
import { formatDate } from 'utils/date.utils';
import { logError } from 'utils/logger.utils';

/**
 * Normalize reportAddress (city/locality, state/adminLevel1, zip/postalcode) for display.
 * @param {Object} reportAddress
 * @param {{ fallback?: string|null }} [options]
 * @returns {string|null}
 */
export const formatSitemapReportAddress = (reportAddress, { fallback = null } = {}) => {
  if (!reportAddress?.address) return fallback;
  try {
    return formatAddress({
      address: reportAddress.address,
      city: reportAddress.city || reportAddress.locality,
      state: reportAddress.state || reportAddress.adminLevel1,
      zip: reportAddress.zip || reportAddress.postalcode,
    });
  } catch (error) {
    logError('formatSitemapReportAddress failed', { error: error.message });
    return fallback;
  }
};

export const isPendingSitemapStatus = (reportDetails) => (
  Number(reportDetails?.statusID) === STATUSES.Pending
  && Number(reportDetails?.subStatusID) === SUB_STATUSES.SiteMap
);

export const shouldRedirectFromSitemapApproval = (reportDetails) => {
  if (reportDetails?.statusID == null || reportDetails?.subStatusID == null) return false;
  return !isPendingSitemapStatus(reportDetails);
};

export const getSitemapSubmittedPath = (encryptedReportId) => (
  `${SITEMAP_SUBMITTED_ROUTE}/${encodeURIComponent(encryptedReportId)}`
);

export const formatSitemapOrderPlaced = (orderPlacedDate, { includeTime } = {}) => {
  if (!orderPlacedDate) return '—';
  const format = includeTime ? 'MMM DD, YYYY, h:mm A' : 'MMM DD, YYYY';
  const formatted = formatDate(orderPlacedDate, format);
  if (formatted) return formatted;
  const parsed = moment.utc(orderPlacedDate);
  if (parsed.isValid()) return parsed.format(format);
  return orderPlacedDate;
};

export const formatSitemapEstimatedCost = (estimatedCost) => {
  if (estimatedCost == null || estimatedCost === '') return '—';
  if (typeof estimatedCost === 'string' && estimatedCost.trim().startsWith('$')) {
    return estimatedCost;
  }
  const amount = Number(estimatedCost);
  if (Number.isNaN(amount)) return '—';
  return `$${amount.toFixed(2)}`;
};
